import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useLateFeeRules, useCreateLateFeeRule, useApplyLateFees } from '@/data/billing';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/billing/late-fees')({
  component: LateFeesPage,
});

function LateFeesPage() {
  const rules = useLateFeeRules();
  const create = useCreateLateFeeRule();
  const apply = useApplyLateFees();
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [grace, setGrace] = useState(5);
  const [feeCents, setFeeCents] = useState(1500);

  async function handleAdd() {
    try {
      await create.mutateAsync({
        name: name.trim(),
        grace_period_days: grace,
        fee_cents: feeCents,
        applies_to: ['tuition'],
        active: true,
      });
      setName('');
      setShowAdd(false);
      toast({ title: 'Late fee rule added', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function handleApply(ruleId: string) {
    try {
      const count = await apply.mutateAsync(ruleId);
      toast({
        title: count > 0 ? `${count} late fees posted` : 'No charges needed late fees',
        description:
          count > 0 ? 'Affected families will see them in their next ledger view.' : undefined,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: "Couldn't apply",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-8">
      <header>
        <Link
          to="/admin/billing"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Billing
        </Link>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <h1>Late fee rules</h1>
          <Button onClick={() => setShowAdd((v) => !v)}>{showAdd ? 'Cancel' : 'New rule'}</Button>
        </div>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Run a rule to auto-post late fees to every overdue charge in its scope. The action is
          idempotent — running twice doesn't double-post.
        </p>
      </header>

      {showAdd && (
        <section className="surface-elevated grid gap-3 p-5 sm:grid-cols-3">
          <div>
            <Label htmlFor="lf-name">Name</Label>
            <Input
              id="lf-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Standard $15 late fee"
            />
          </div>
          <div>
            <Label htmlFor="lf-grace">Grace days</Label>
            <Input
              id="lf-grace"
              type="number"
              min="0"
              max="90"
              value={grace}
              onChange={(e) => setGrace(Number.parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div>
            <Label htmlFor="lf-fee">Fee (cents)</Label>
            <Input
              id="lf-fee"
              type="number"
              min="1"
              value={feeCents}
              onChange={(e) => setFeeCents(Number.parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="sm:col-span-3">
            <Button
              onClick={() => void handleAdd()}
              disabled={!name.trim() || feeCents <= 0 || create.isPending}
            >
              {create.isPending ? 'Saving…' : 'Save rule'}
            </Button>
          </div>
        </section>
      )}

      {rules.data?.length === 0 && !showAdd && (
        <Empty
          title="No late fee rules yet"
          description="Create a rule to automate late-fee posting on overdue tuition."
        />
      )}

      {rules.data && rules.data.length > 0 && (
        <ul className="grid gap-3">
          {rules.data.map((r) => (
            <li key={r.id} className="surface-elevated p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <h2 className="font-[var(--font-display)] text-[var(--text-lg)]">{r.name}</h2>
                  <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    {r.fee_cents
                      ? `$${(r.fee_cents / 100).toFixed(2)} fee`
                      : `${(r.fee_percent_bp ?? 0) / 100}% of charge`}{' '}
                    · {r.grace_period_days} day grace · applies to {r.applies_to.join(', ')}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {r.active ? (
                    <Badge tone="success">active</Badge>
                  ) : (
                    <Badge tone="neutral">inactive</Badge>
                  )}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void handleApply(r.id)}
                    disabled={!r.active || apply.isPending}
                  >
                    {apply.isPending ? 'Running…' : 'Run now'}
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
